// Conversation Types

// QA Label Types - Dual Signal Schema (v2)
export type BusinessOutcome = 'success' | 'failure' | 'partial' | 'neutral'
export type TrainingSuitability = 'keep' | 'review' | 'discard'
export type ViolationSeverity = 'minor' | 'major'

export type QALabelSource = 'human' | 'llm'

// Violation codes are loaded dynamically from the backend via /conversations/qa-config/violation-codes
export interface Violation {
  code: string
  severity: ViolationSeverity
  description: string
}

export interface QALabel {
  // Schema version
  version: 'v2'

  // Business Outcome (lenient) - PRIMARY for UI display
  business_outcome: BusinessOutcome
  outcome_notes: string

  // Training Suitability (strict) - for data pipeline
  training_suitability: TrainingSuitability
  adherence_score: number  // 1-10
  violations: Violation[]

  // Metadata
  labeled_at: string
  labeled_by: number | string
  source: QALabelSource
  model?: string        // LLM model name (e.g., "gpt-4o") - only for source: 'llm'
  confidence?: number   // 0-1 confidence score - optional, for LLM labels
}


export type OutcomeType =
  | 'no_action'
  | 'information_provided'
  | 'business_follow_up'
  | 'completed'
  | 'caller_follow_up'
  | 'transferred'
  | 'answered_by_business'
  | 'missed_by_business'

export type MessageRole = 'system' | 'assistant' | 'tool' | 'user'

export interface ToolCall {
  id: string
  function: {
    name: string
    arguments: string
  }
  type: string
}

export interface Message {
  role: MessageRole
  content: string | null
  tool_calls?: ToolCall[]
  tool_call_id?: string
  refusal?: string | null
}

// Partner review of a single assistant turn (admin-only feature)
export interface PartnerReview {
  message_index: number
  flagged: boolean
  comment: string
  user_id: number
  updated_at: string
}

export interface PartnerReviewContext {
  reviewable: boolean
  reason?: 'not_configured' | 'not_reviewable' | 'logs_expired' | null
  reviewable_indices: number[]
  reviews: PartnerReview[]
}

export interface ConversationMeta {
  recording_url?: string
  rootPath?: string
  rating?: number
  whatsapp_messages?: {
    inbound?: boolean
    outbound?: boolean
  }
  qa_label?: QALabel
  [key: string]: unknown
}

export interface Conversation {
  id: string | number
  created_at: string
  updated_at: string
  outcome: OutcomeType
  summary?: string
  topic?: string
  phone_number?: string
  from_id?: string
  from_name?: string
  duration?: number
  messages?: Message[] | string
  audio_url?: string
  tags?: string[]
  assistant_id?: number
  assistant_name?: string
  meta?: string
  twilio_id?: string
}

export type ConversationChannel = 'phone' | 'whatsapp'

export interface ConversationListFilters {
  assistant_id?: number[]
  customer_id?: number[]
  channel?: ConversationChannel
  outcome?: OutcomeType
  tags?: string[]
  date_from?: string
  date_to?: string
  topic?: string
  summary?: string
  from_id?: string
  booking_system?: string[]
}

export interface ConversationListResponse {
  data: Conversation[]
  total: number
  page: number
  perPage: number
}

// Notes Types
export type PriorityLevel = 'low' | 'medium' | 'high' | 'critical'
export type NoteStatus = 'none' | 'open' | 'closed'

export interface NoteReply {
  id: string
  content: string
  created_by: number
  created_by_user?: {
    id: number
    first_name: string
    last_name: string
  }
  created_at: string
  updated_at: string | null
  note_id: string
  is_private: boolean
}

export interface Note {
  id: string
  content: string
  status: NoteStatus
  category?: string
  priority?: PriorityLevel
  is_private: boolean
  created_by: number
  created_by_user?: {
    id: number
    first_name: string
    last_name: string
  }
  created_at: string
  updated_at: string
  assigned_to: number | null
  assigned_to_user?: {
    id: number
    first_name: string
    last_name: string
  }
  replies?: NoteReply[]
  assigned_team_id?: number | null
  assigned_team?: {
    id: number
    name: string
  }
}

// Outcome configuration
export interface OutcomeConfig {
  color: string
  bgColor: string
  label: string
}

export const outcomeConfig: Record<OutcomeType, OutcomeConfig> = {
  no_action: {
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-50 dark:bg-red-950',
    label: 'No Action',
  },
  information_provided: {
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-50 dark:bg-blue-950',
    label: 'Information Provided',
  },
  business_follow_up: {
    color: 'text-orange-600 dark:text-orange-400',
    bgColor: 'bg-orange-50 dark:bg-orange-950',
    label: 'Business Follow-up',
  },
  completed: {
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-50 dark:bg-green-950',
    label: 'Completed',
  },
  caller_follow_up: {
    color: 'text-indigo-600 dark:text-indigo-400',
    bgColor: 'bg-indigo-50 dark:bg-indigo-950',
    label: 'Caller Follow-up',
  },
  transferred: {
    color: 'text-gray-600 dark:text-gray-400',
    bgColor: 'bg-gray-100 dark:bg-gray-800',
    label: 'Transferred',
  },
  answered_by_business: {
    color: 'text-emerald-600 dark:text-emerald-400',
    bgColor: 'bg-emerald-50 dark:bg-emerald-950',
    label: 'Answered by Business',
  },
  missed_by_business: {
    color: 'text-rose-600 dark:text-rose-400',
    bgColor: 'bg-rose-50 dark:bg-rose-950',
    label: 'Missed by Business',
  },
}

// Tags configuration - basic tags available to all users
export const basicTags: Record<string, string> = {
  ':thumbs-up:': 'Thumbs up',
  ':thumbs-down:': 'Thumbs down',
}

// Tags configuration - admin/partner only tags
export const adminTags: Record<string, string> = {
  ':needs-review:': 'Needs review',
  ':reviewed:': 'Reviewed',
  ':finetuning:': 'Finetuning',
  ':example:': 'Example',
}

// All tags combined (for display purposes)
export const allTagsWithEmoji: Record<string, string> = {
  ...basicTags,
  ...adminTags,
}
